import useShareService from './useShareService';
import { ColorTextureLayer, TextTextureLayer } from '../layers';
import omit from "lodash/omit";
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';

NProgress.configure({ showSpinner: false });

interface CartProgressDetail {
  phase: string;
  message?: string;
  current?: number;
  total?: number;
}

function dispatchCartProgress(detail: CartProgressDetail) {
  window.dispatchEvent(new CustomEvent('cart:progress', { detail }));
}

type ExporterActionType = (
  data: {},
  onSuccess: (...args: any[]) => void,
  onError: (message?: string) => void,
) => void;

export default function useExporter(builder) {
  const { encode } = useShareService();

  // Read edit-mode params from sessionStorage (captured at module load in App.tsx
  // before pushState strips URL params). Falls back to membersData and URL.
  const getBuilderAction = () => (sessionStorage.getItem('builder_action') || builder?.membersData?.action || "") as "edit"|"update"|string;
  const isUpdateAction = getBuilderAction() === "update";
  const projectId = Number(sessionStorage.getItem('builder_project_id') || builder?.membersData?.projectId || 0) || 0;

  const getAnyExistingBuildId = (): number | null => {
    if (isUpdateAction && projectId) return projectId;
    const root = builder?.membersData?.rootSavedBuildId || null;
    if (root) return root;
    const m = builder?.membersData?.members?.find((x:any) => x?.saved_builds_id);
    return m?.saved_builds_id || null;
  };

  const setRootBuildId = (id?: number) => {
    if (!id) return;
    if (!builder.membersData.rootSavedBuildId) {
      builder.membersData.setRootSavedBuildId?.(id);
    }
  };

  const shouldUseSameBuild = (memberIndex: number): boolean => {
    if (memberIndex === 0) return false;
    return true;
  };

  return async (
    action: ExporterActionType,
    externalOnSuccess: (...args: any[]) => void,
    onError: (message?: string) => void,
    actionName?: string
  ) => {
    const { membersData, imagesData, layersData, manifest } = builder;

    // Read edit-mode params from sessionStorage (set at module load in App.tsx).
    // Falls back to membersData and URL params.
    const builderAction = sessionStorage.getItem('builder_action') || membersData?.action || "";
    const isUpdateAction = builderAction === "update";
    const projectId = Number(sessionStorage.getItem('builder_project_id') || membersData?.projectId || 0) || 0;
    const cartitemKeyFromUrl = sessionStorage.getItem('builder_cart_data') || membersData?.cartData || '';

    if (isUpdateAction && projectId && !getAnyExistingBuildId()) {
      setRootBuildId(projectId);
    }

    const findCmyk = (findHex) => manifest.palette
      .flatMap(p => p.palette)
      .reduce((acc, [hex, cmyk]) => (hex === findHex ? cmyk : acc), '');

    const colorsData = {
      layers: layersData
        .filter(item => item.layer instanceof ColorTextureLayer)
        .map(item => ({
          id: item.id,
          hex: item.layer.color,
          cmyk: findCmyk(item.layer.color),
          pattern: item.layer.patternFile
        })),
      text: layersData
        .filter(item => item.layer instanceof TextTextureLayer)
        .map(item => ({
          id: item.id,
          hex: item.layer.color,
          cmyk: findCmyk(item.layer.color)
        }))
    };

    const sponsorImages = (obj) => {
      const result = {};
      for (let key in obj) {
        if (obj.hasOwnProperty(key)) {
          result[key] = obj[key].map(item => {
            if (item) {
              item = omit(item, ["image", "loading"]);
              item.layerMeta = omit(item.layerMeta, ["image"]);
            }
            return item;
          });
        }
      }
      return result;
    };

    const singleImage = (obj) => omit(obj, ["image", "loading"]);

    const sfTp = (vl) => {
      let res = {};
      builder.modelData.surffaceList.map(item => {
        if (item.id === vl) res = omit(item, ["surface_title"]);
      });
      return res;
    };

  const buildMemberPayload = (
    m: any,
    index: number,
    cameraList: any,
    view: 'back' | 'all'
  ) => {
    const safe = (side: 'front' | 'back' | 'left' | 'right') =>
      cameraList?.[index]?.[side] || {};

    const model_preview =
      view === 'back'
        ? { back: safe('back'), front: {}, left: {}, right: {} }
        : { front: safe('front'), back: safe('back'), left: safe('left'), right: safe('right') };

    return {
      ...m,
      text: Object.keys(m?.text || {}).map(textId => ({
        ...m.text[textId],
        color: membersData.memberTextColors[textId],
        color_cmyk: findCmyk(membersData.memberTextColors[textId]),
        family: membersData.memberFontFamily,
        computedFontSize:
          membersData.membersFontSize?.[index]?.[textId] ??
          m.text[textId]?.computedFontSize ?? 0,
      })),
      model_preview,
    };
  };

    // Unified single-request add-to-cart: sends ALL data (members with all 4 views,
    // images, layers, colors, share data) in one request. PHP processes step 1 (create
    // cart items) then step 2 (save full customization data) in the same request.
    const sendUnifiedData = async (cameraList) => {
      const idx = membersData.currentMemberIndex;
      const rootBuildId = getAnyExistingBuildId();
      const isSameBuild = shouldUseSameBuild(idx);
      const members = membersData.members.map((m, i) =>
        buildMemberPayload(m, i, cameraList, 'all')
      );

      const payload: any = {
        membersData: members,
        imagesData: {
          sponsorImages: sponsorImages(imagesData.sponsorImages),
          sponsorFlag: { ...imagesData.sponsorFlag },
          image: singleImage(imagesData.image),
          mouseSurface: sfTp(builder.modelData.surfface),
        },
        layersData: layersData
          .filter(item => item.layer.visible)
          .map(item => ({
            id: item.id,
            type: item.type,
            position: item.position,
            layer: omit(item.layer.toJSON(), ["image"]),
          })),
        step: 1,
        unifiedRequest: true,
        colorsData,
        shareData: encode(builder),
        quantity: builder.membersData.totalSum(),
        total: builder.manifest.peritemPrice * builder.membersData.totalSum(),
        productId: manifest?.productId,
        userId: builder.curentUserid,
        editAction: builder.membersData.editAction,
        patternSvgId: builder.modelData.patternSvgId,
        order_id: '',
        isSameBuild,
      };

      if (isUpdateAction) {
        payload.saved_builds_id = projectId;
        payload.cart_data = cartitemKeyFromUrl;
      } else if (isSameBuild && rootBuildId) {
        payload.saved_builds_id = rootBuildId;
      }

      return new Promise<any[]>((resolve, reject) => {
        action(
          payload,
          (...cbArgs: any[]) => {
            try {
              const [
                res,
                cart_key,
                messageBtn,
                messageUnBtn,
                countCart,
                saved_builds_id
              ] = cbArgs || [];

              setRootBuildId(saved_builds_id);

              const mem: any = membersData.members[idx];
              mem.saved_builds_id = builder.membersData.rootSavedBuildId || saved_builds_id || null;
              mem.cart_key = cart_key;
              mem.removeKey = cart_key || cartitemKeyFromUrl || '';

              window.dispatchEvent(new CustomEvent("cart:updated"));

              resolve(cbArgs);
            } catch (e) {
              reject(e);
            }
          },
          (err) => { onError?.(err); reject(err); }
        );
      });
    };

    const exportSaveData = (cameraList) => {
      // Debug: verify preview data before sending to save handler
      if (cameraList) {
        cameraList.forEach((preview, idx) => {
          const backFp = preview?.back ? preview.back.substring(preview.back.length - 40) : 'EMPTY';
          console.log(`[exportSaveData] Member ${idx} back preview fingerprint: ...${backFp}`);
        });
        if (cameraList.length >= 2 && cameraList[0]?.back && cameraList[1]?.back) {
          console.log(`[exportSaveData] Previews identical? ${cameraList[0].back === cameraList[1].back}`);
        }
      }
      return action({
      membersData: [...membersData.members.map((m, index) => (
        omit({
          ...m,
          text: Object.keys(m.text).map(textId => ({
            ...m.text[textId],
            color: membersData.memberTextColors[textId],
            color_cmyk: findCmyk(membersData.memberTextColors[textId]),
            family: membersData.memberFontFamily,
            computedFontSize: membersData.membersFontSize[index][textId]
          })),
          model_preview: cameraList[index],
        }, ["preview"])
      ))],
      imagesData: {
        sponsorImages: sponsorImages(imagesData.sponsorImages),
        sponsorFlag: { ...imagesData.sponsorFlag },
        image: singleImage(imagesData.image),
        mouseSurface:  sfTp(builder.modelData.surfface),
      },
      layersData: [
        ...layersData
          .filter(item => item.layer.visible)
          .map(item => ({
            id: item.id,
            type: item.type,
            position: item.position,
            layer: omit(item.layer.toJSON(), ["image"]),
          }))
      ],
      colorsData,
      quantity: builder.membersData.totalSum(),
      total: (builder.manifest.peritemPrice * builder.membersData.totalSum()),
      productId: manifest?.productId,
      userId: builder.curentUserid,
      editAction: builder.membersData.editAction,
      shareData: encode(builder),
      patternSvgId: builder.modelData.patternSvgId
    }, externalOnSuccess, onError);
    };

    // =========================================================================
    // NEW: SAVED BUILD REFERENCE FLOW
    // =========================================================================
    //
    // Instead of per-member step 1/step 2 that duplicates data and overwrites
    // previews, this flow:
    //   1. Saves design ONCE → get design_id
    //   2. Adds ALL members to cart in a single batch request
    //
    // Activated when bridge functions (builderSaveDesign, builderAddToCartBatch)
    // are available on the page. Falls back to legacy flow otherwise.
    // =========================================================================

    /**
     * Build a lightweight member object for the batch cart request.
     * Only includes per-member data (name, number, size, etc.) — NOT the full design.
     */
    const buildLightweightMember = (m: any, index: number, cameraList: any) => {
      const backPreview = cameraList?.[index]?.['back'] || '';
      const frontPreview = cameraList?.[index]?.['front'] || '';

      return {
        name: m?.text?.fullname?.value || m?.text?.title?.value || '',
        number: m?.text?.number?.value || '',
        size: m?.data?.size || '',
        qty: m?.data?.quantity || 1,
        sleeve: m?.data?.sleeve || '',
        sex: m?.data?.sex || '',
        // Include enriched text array for order meta (font, color, CMYK)
        text: Object.keys(m?.text || {}).map(textId => ({
          ...m.text[textId],
          color: membersData.memberTextColors[textId],
          color_cmyk: findCmyk(membersData.memberTextColors[textId]),
          family: membersData.memberFontFamily,
          computedFontSize:
            membersData.membersFontSize?.[index]?.[textId] ??
            m.text[textId]?.computedFontSize ?? 0,
        })),
        backPreview,
        frontPreview,
      };
    };

    /**
     * Save the design to the server (once) and return a design_id.
     */
    const saveDesignReference = async (cameraList: any): Promise<number> => {
      dispatchCartProgress({ phase: 'saving_design', message: 'Saving design...' });

      // Use the first member's previews as the representative images
      const representativePreview = cameraList?.[0]?.['back'] || '';
      const representativeFrontPreview = cameraList?.[0]?.['front'] || '';

      const designPayload: any = {
        productId: manifest?.productId,
        patternSvgId: builder.modelData.patternSvgId,
        imagesData: {
          sponsorImages: sponsorImages(imagesData.sponsorImages),
          sponsorFlag: { ...imagesData.sponsorFlag },
          image: singleImage(imagesData.image),
          mouseSurface: sfTp(builder.modelData.surfface),
        },
        layersData: layersData
          .filter(item => item.layer.visible)
          .map(item => ({
            id: item.id,
            type: item.type,
            position: item.position,
            layer: omit(item.layer.toJSON(), ["image"]),
          })),
        colorsData,
        shareData: encode(builder),
        quantity: builder.membersData.totalSum(),
        total: builder.manifest.peritemPrice * builder.membersData.totalSum(),
        backPreview: representativePreview,
        frontPreview: representativeFrontPreview,
      };

      // For update mode: pass existing design ID so PHP updates it instead of creating new
      if (isUpdateAction && projectId) {
        designPayload.existing_design_id = projectId;
      }

      const saveDesignFn = (window as any).builderSaveDesign;
      if (!saveDesignFn) {
        throw new Error('builderSaveDesign not available — bridge function missing');
      }

      const response = await saveDesignFn(designPayload);

      if (!response?.design_id) {
        throw new Error('Failed to save design: no design_id returned');
      }

      return response.design_id;
    };

    /**
     * Add all members to cart in a single batch request, referencing the saved design_id.
     */
    const addToCartBatch = async (designId: number, cameraList: any): Promise<any> => {
      const totalMembers = membersData.members.length;
      dispatchCartProgress({
        phase: 'adding_to_cart',
        current: 0,
        total: totalMembers,
        message: `Adding ${totalMembers} item${totalMembers > 1 ? 's' : ''} to cart...`,
      });

      const members = membersData.members.map((m, i) =>
        buildLightweightMember(m, i, cameraList)
      );

      const batchPayload: any = {
        design_id: designId,
        productId: manifest?.productId,
        members,
      };

      // For update mode: tell the server to remove old cart items before adding new ones
      if (isUpdateAction && projectId) {
        batchPayload.remove_design_id = projectId;
      }

      const addToCartFn = (window as any).builderAddToCartBatch;
      if (!addToCartFn) {
        throw new Error('builderAddToCartBatch not available — bridge function missing');
      }

      const response = await addToCartFn(batchPayload);

      if (response?.status !== 'ok') {
        throw new Error(response?.message || 'Failed to add items to cart');
      }

      return response;
    };

    /**
     * New reference-based submission flow.
     * 1. Generate previews for all members
     * 2. Save the design once → design_id
     * 3. Add all members to cart in a single batch request
     * 4. Update member state with cart keys
     */
    const executeReferenceFlow = async (previewData: any) => {
      try {
        NProgress.start();

        // Step 1: Save design once
        const designId = await saveDesignReference(previewData);
        setRootBuildId(designId);

        // Step 2: Add all members to cart in one batch
        const response = await addToCartBatch(designId, previewData);

        // Step 3: Update member state with results
        const cartKeys = response.cart_keys || [];
        const idx = membersData.currentMemberIndex;

        membersData.members.forEach((m: any, i: number) => {
          m.saved_builds_id = designId;
          m.cart_key = cartKeys[i] || '';
          m.removeKey = cartKeys[i] || '';
          m.resultAddedCart = true;
          m.messageBtn = response.messageBtn || 'THIS IS ITEM HAS BEEN ADDED TO CART';
          m.messageUnBtn = response.messageUnBtn || '';
        });

        dispatchCartProgress({
          phase: 'complete',
          current: membersData.members.length,
          total: membersData.members.length,
          message: `${membersData.members.length} item${membersData.members.length > 1 ? 's' : ''} added to cart!`,
        });

        window.dispatchEvent(new CustomEvent("cart:updated"));

        // Notify success — call the external callback with the same signature
        // as the legacy flow for backward compatibility
        externalOnSuccess?.(
          true,                               // res (result)
          cartKeys[idx] || cartKeys[0] || '',  // cart_key
          response.messageBtn || 'THIS IS ITEM HAS BEEN ADDED TO CART',
          response.messageUnBtn || '',
          response.countCart || 0,
          designId,                            // saved_builds_id
        );

      } catch (error: any) {
        console.error('Error during reference flow:', error);
        const errorMsg = error instanceof Error
          ? error.message
          : (error?.message || error?.responseText || 'Failed to add items to cart');
        dispatchCartProgress({
          phase: 'error',
          message: errorMsg,
        });
        onError?.(errorMsg);
      } finally {
        NProgress.done();
      }
    };


    // =========================================================================
    // EXECUTION: Choose the appropriate flow
    // =========================================================================

    const executeRequests = async () => {
      try {
        NProgress.start();

        if (actionName === "onSave") {
          // Use getMembersPreview to render ALL members' previews (not just the current one).
          // getQuickPreview only renders the current member, leaving other members' model_preview
          // undefined — which causes broken images when publishing saved builds.
          const allPreviews = await builder?.getMembersPreview();
          await exportSaveData(allPreviews);
          return;
        }

        // Check if the new reference-based bridge functions are available.
        const hasBridgeFunctions =
          typeof (window as any).builderSaveDesign === 'function' &&
          typeof (window as any).builderAddToCartBatch === 'function';

        if (hasBridgeFunctions) {
          // Reference flow — render ALL members, save design once, batch add to cart.
          // Only renders back+front views (left/right are unused in cart flow) with
          // slightly lower JPEG quality to reduce payload size and encoding time.
          // Resolution stays at 605×725 since these images may be used as store product images.
          dispatchCartProgress({ phase: 'generating_previews', message: 'Generating previews...' });
          const allPreviews = await builder?.getMembersPreview(
            ["back", "front"],
            { width: 605, height: 725 },
            0.85
          );
          await executeReferenceFlow(allPreviews);
        } else {
          // LEGACY: unified single-request flow (fallback for old PHP templates)
          const quickPreview = await builder?.getQuickPreview();
          const args = await sendUnifiedData(quickPreview);
          externalOnSuccess?.(...args);
        }

      } catch (error) {
        console.error('Error during request execution:', error);
      } finally {
        NProgress.done();
      }
    };

    executeRequests();
  };
}
